import { db, functions, auth } from '../config/firebase';
import { doc, updateDoc, serverTimestamp, getDoc, addDoc, setDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import MERCADO_PAGO_CONFIG from '../config/mercadopago';

interface PaymentData {
  empresaId: string;
  valor: number;
  descricao: string;
  parcelaId?: string;
  cobrancaRecorrenteId?: string;
  userId?: string;
}

interface PaymentPreference {
  id: string;
  init_point: string;
  sandbox_init_point: string;
}

export const paymentService = {
  // Monitorar status do pagamento em tempo real
  async watchPaymentStatus(preferenceId: string, callbacks: {
    onPending?: () => void,
    onApproved?: () => void,
    onRejected?: () => void,
    onError?: (error: any) => void
  }) {
    try {
      console.log('Iniciando monitoramento do pagamento:', preferenceId);
      
      const transacoesRef = collection(db, 'transacoesCarteira');
      const q = query(
        transacoesRef,
        where('referenciaPagamento', '==', preferenceId)
      );

      // Monitorar mudanças em tempo real
      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const transacao = snapshot.docs[0].data();
          console.log('Status atual da transação:', transacao.status);

          switch (transacao.status) {
            case 'pendente':
              console.log('Chamando callback onPending');
              callbacks.onPending?.();
              break;
            case 'aprovado':
              console.log('Chamando callback onApproved');
              callbacks.onApproved?.();
              break;
            case 'rejeitado':
              console.log('Chamando callback onRejected');
              callbacks.onRejected?.();
              break;
            default:
              console.log('Status desconhecido:', transacao.status);
          }
        } else {
          console.log('Nenhuma transação encontrada para o preferenceId:', preferenceId);
        }
      }, (error) => {
        console.error('Erro ao monitorar pagamento:', error);
        callbacks.onError?.(error);
      });

      // Retornar função para parar o monitoramento
      return unsubscribe;
    } catch (error) {
      console.error('Erro ao configurar monitoramento:', error);
      callbacks.onError?.(error);
    }
  },

  // Criar uma preferência de pagamento no Mercado Pago via Cloud Function
  // As credenciais são armazenadas de forma segura no backend
  async createPaymentPreference(data: PaymentData) {
    try {
      console.log('Criando preferência de pagamento via Cloud Function:', data);
      
      // Verificar se o usuário está autenticado no Firebase Auth
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [PAYMENT] Usuário não autenticado no Firebase Auth');
        throw new Error('Você precisa estar autenticado para criar um pagamento.');
      }
      
      console.log('✅ [PAYMENT] Usuário autenticado:', currentUser.uid);
      
      // Validar se temos os dados necessários
      if (!data.empresaId || !data.valor || data.valor <= 0) {
        throw new Error('Dados de pagamento inválidos');
      }

      // Chamar a Cloud Function que armazena as credenciais de forma segura
      // O httpsCallable automaticamente passa o token de autenticação se o usuário estiver autenticado
      const createPaymentPreferenceFunction = httpsCallable(functions, 'createPaymentPreference');
      
      const result = await createPaymentPreferenceFunction({
        empresaId: data.empresaId,
        valor: data.valor,
        descricao: data.descricao,
        parcelaId: data.parcelaId,
        cobrancaRecorrenteId: data.cobrancaRecorrenteId,
        userId: data.userId || data.empresaId,
        origin: window.location.origin,
      });

      const response = result.data as {
        preferenceId: string;
        initPoint: string;
        transacaoId: string;
      };

      console.log('Preferência criada via Cloud Function:', response);

      // Retornar dados no formato esperado pelo frontend
      return {
        id: response.preferenceId,
        init_point: response.initPoint,
        sandbox_init_point: response.initPoint,
        transacaoId: response.transacaoId,
      };
    } catch (error: any) {
      console.error('Erro ao criar preferência de pagamento:', error);
      
      // Tratar erros específicos da Cloud Function
      if (error.code === 'functions/unauthenticated') {
        throw new Error('Você precisa estar autenticado para criar um pagamento.');
      } else if (error.code === 'functions/invalid-argument') {
        throw new Error(error.message || 'Dados de pagamento inválidos.');
      } else if (error.code === 'functions/not-found') {
        throw new Error('Empresa não encontrada.');
      } else if (error.code === 'functions/internal') {
        throw new Error('Erro interno do servidor. Tente novamente mais tarde.');
      }
      
      throw new Error(error.message || 'Erro ao criar preferência de pagamento.');
    }
  },

  // Marcar parcela como paga quando o pagamento for confirmado
  async markParcelaAsPaid(empresaId: string, cobrancaRecorrenteId: string, parcelaNumero: number) {
    try {
      console.log('Marcando parcela como paga:', { empresaId, cobrancaRecorrenteId, parcelaNumero });
      
      // Buscar a cobrança recorrente da empresa
      const cobrancaRef = doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', 'ativa');
      const cobrancaDoc = await getDoc(cobrancaRef);
      
      if (!cobrancaDoc.exists()) {
        throw new Error('Cobrança recorrente não encontrada');
      }
      
      const cobrancaData = cobrancaDoc.data();
      const parcelas = cobrancaData.parcelas || [];
      
      // Encontrar e atualizar a parcela específica
      const parcelaIndex = parcelas.findIndex((p: any) => p.numero === parcelaNumero);
      
      if (parcelaIndex === -1) {
        throw new Error('Parcela não encontrada');
      }
      
      // Atualizar a parcela como paga
      parcelas[parcelaIndex] = {
        ...parcelas[parcelaIndex],
        status: 'paga',
        dataPagamento: serverTimestamp()
      };
      
      // Atualizar a cobrança recorrente
      await updateDoc(cobrancaRef, {
        parcelas: parcelas,
        parcelasRestantes: parcelas.filter((p: any) => p.status === 'pendente').length,
        ultimaAtualizacao: serverTimestamp()
      });
      
      // Adicionar ao histórico de pagamentos
      const historicoRef = doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', 'historico');
      const historicoDoc = await getDoc(historicoRef);
      
      const historicoData = historicoDoc.exists() ? historicoDoc.data() : { pagamentos: [] };
      const novoPagamento = {
        parcelaNumero: parcelaNumero,
        valor: parcelas[parcelaIndex].valor,
        dataVencimento: parcelas[parcelaIndex].dataVencimento,
        dataPagamento: serverTimestamp(),
        descricao: cobrancaData.descricao || 'Cobrança Recorrente',
        status: 'pago',
        dataRegistro: serverTimestamp()
      };
      
      historicoData.pagamentos.unshift(novoPagamento);
      
      if (historicoDoc.exists()) {
        await updateDoc(historicoRef, historicoData);
      } else {
        await setDoc(historicoRef, historicoData);
      }
      
      console.log('Parcela marcada como paga com sucesso');
    } catch (error) {
      console.error('Erro ao marcar parcela como paga:', error);
      throw error;
    }
  },
};

